import { useUpdates } from 'context/UpdatesContext.jsx';
import { Button } from 'components/common';

/**
 * The one-time "What's New" notice.
 *
 * One heading, then every unseen entry beneath it with its own title, date and
 * detail list. Shown to every signed-in user: an entry reaches everybody unless
 * it is explicitly marked `audience: 'admin'` in changelog.js.
 *
 * Rendered as the first child of whichever content column is showing, so it
 * scrolls away with the content like any other in-page notice rather than
 * clamping itself over the chrome.
 *
 * Both shells render this in the same position, which is what makes the old
 * MutationObserver unnecessary: a theme switch swaps the shell, and React puts
 * the banner back where the new shell declares it. Nothing has to observe
 * anything. See UpdatesContext for the four bugs that mechanism used to cause.
 */
export default function UpdatesBanner() {
  const { entries, dismiss, hasUpdates } = useUpdates();
  if (!hasUpdates) return null;

  const fmtDate = iso => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? ''
      : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="upd-alert" role="status" aria-labelledby="upd-heading">
      <h2 className="upd-heading" id="upd-heading">What's New</h2>
      {entries.map(e => (
        <div className="upd-entry" key={e.seq}>
          <div className="upd-title">{e.title}</div>
          {e.date && <div className="upd-date">{fmtDate(e.date)}</div>}
          <ul className="upd-list">
            {(e.items ?? []).map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </div>
      ))}
      <div className="upd-foot">
        <Button variant="dismiss" onClick={dismiss}>Got it</Button>
      </div>
    </div>
  );
}
